(() => {
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const reducedMotion = () => document.body?.dataset.acMotion === 'reduced'
    || window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class AcProductRail extends HTMLElement {
    #swiper = null;
    #nearObserver = null;
    #motion = null;
    #cleanup = [];
    #motionReady = false;
    #motionSettleTimer = 0;
    #pointerDown = false;
    #momentumActive = false;
    #motionObserver = null;
    #motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
    #reduced = false;

    connectedCallback() {
      if (this.dataset.profile === 'search' && this.closest('ac-search-overlay')) return;
      if ((document.body.classList.contains('ac-reference--home') || document.querySelector('[data-ac-ref-product]'))
        && !this.closest('.ac-overlay') && !window.Shopify?.designMode
        && !location.hash && !reducedMotion() && 'IntersectionObserver' in window) {
        // Let the document's initial script task finish before carousel layout.
        // A 200px lead keeps enhancement ready as the first product row appears.
        this.#nearObserver = new IntersectionObserver((entries) => {
          if (entries.some(entry => entry.isIntersecting)) this.refresh();
        }, { rootMargin: '200px 0px' });
        this.#nearObserver.observe(this);
      } else this.refresh();
    }

    disconnectedCallback() {
      this.suspend();
    }

    suspend() {
      this.#nearObserver?.disconnect();
      this.#nearObserver = null;
      this.#motionObserver?.disconnect();
      this.#motionObserver = null;
      this.#motionPreference.removeEventListener('change', this.#syncMotion);
      this.#destroy();
    }

    #syncMotion = () => {
      const reduced = reducedMotion();
      if (!this.isConnected || this.#reduced === reduced) return;
      this.#reduced = reduced;
      this.#motion?.revert?.();
      this.#motion = null;
      this.#motionReady = true;
      if (this.#swiper) {
        const position = this.#swiper.getTranslate();
        this.#swiper.setTransition(0);
        this.#swiper.setTranslate(position);
        this.#swiper.animating = false;
        for (const params of [this.#swiper.params, this.#swiper.originalParams]) {
          params.freeMode.enabled = !reduced;
          params.speed = reduced ? 0 : 420;
          params.grabCursor = !reduced;
        }
        if (reduced) this.#swiper.unsetGrabCursor();
        else this.#swiper.setGrabCursor();
      } else {
        const viewport = this.querySelector('[data-ac-product-list-viewport]');
        if (viewport) viewport.scrollTo({ left: viewport.scrollLeft, behavior: 'instant' });
      }
      window.clearTimeout(this.#motionSettleTimer);
      this.#motionSettleTimer = 0;
      this.#pointerDown = false;
      this.#momentumActive = false;
      if (this.hasAttribute('data-ac-product-rail-moving')) {
        this.removeAttribute('data-ac-product-rail-moving');
        this.dispatchEvent(new CustomEvent('ac:product-rail-motion-end', { bubbles: true }));
      }
    };

    refresh() {
      if (!this.isConnected) return;
      this.#nearObserver?.disconnect();
      this.#nearObserver = null;
      this.#destroy();
      this.#reduced = reducedMotion();
      if (!this.#motionObserver) {
        this.#motionObserver = new MutationObserver(this.#syncMotion);
        this.#motionObserver.observe(document.body, { attributes: true, attributeFilter: ['data-ac-motion'] });
        this.#motionPreference.addEventListener('change', this.#syncMotion);
      }
      const search = this.dataset.profile === 'search';
      if (search) this.removeAttribute('data-overflow');
      if (search && this.closest('[hidden], [aria-hidden="true"]')) return;
      const list = this.querySelector('[data-ac-product-list]');
      const viewport = list?.querySelector('[data-ac-product-list-viewport]');
      const track = list?.querySelector('[data-ac-product-list-track]');
      const items = list ? [...list.querySelectorAll('.ac-product-list__item')] : [];
      if (!list || !viewport || !track || !items.length) return;

      const progress = this.querySelector('[data-ac-progress]');
      const labels = [...this.querySelectorAll('[data-ac-product-rail-label]')];
      const reduced = this.#reduced;
      const compact = this.dataset.profile === 'compact';
      const setProgressTransition = (duration = 0) => {
        if (!progress) return;
        const safeDuration = reducedMotion() ? 0 : Math.max(Number(duration) || 0, 0);
        progress.style.setProperty('--ac-progress-motion-duration', `${safeDuration}ms`);
        progress.toggleAttribute('data-ac-progress-animating', safeDuration > 0);
      };
      let lastPageText = '';
      let lastRatio = -1;
      let lastPercent = -1;
      const sync = (ratio, page, pages) => {
        ratio = clamp(ratio, 0, 1);
        page = clamp(page, 0, pages - 1);
        const pageText = `${page + 1} / ${pages}`;
        if (pageText !== lastPageText) {
          for (const [labelIndex, label] of labels.entries()) {
            label.classList.toggle('is-active', labelIndex === page % labels.length);
          }
          progress?.setAttribute('aria-valuetext', pageText);
          lastPageText = pageText;
        }
        if (progress) {
          if (ratio !== lastRatio) progress.style.setProperty('--ac-progress-value', String(ratio));
          const percent = Math.round(ratio * 100);
          if (percent !== lastPercent) progress.setAttribute('aria-valuenow', String(percent));
          lastRatio = ratio;
          lastPercent = percent;
        }
      };
      const syncSwiper = (instance) => {
        const pages = Math.max(instance.snapGrid?.length || 1, 1);
        sync(Number.isFinite(instance.progress) ? instance.progress : 0, instance.snapIndex || 0, pages);
        if (search) this.toggleAttribute('data-overflow', !instance.isLocked && pages > 1);
      };

      const gap = () => window.innerWidth < 1024 ? 10 : window.innerWidth / 144;
      if (window.Swiper) {
        list.classList.add('is-swiper');
        viewport.classList.add('swiper');
        viewport.setAttribute('aria-roledescription', 'carousel');
        track.classList.add('swiper-wrapper');
        for (const item of items) item.classList.add('swiper-slide');
        const syncGap = (instance) => {
          const currentGap = gap();
          instance.params.spaceBetween = currentGap;
          instance.originalParams.spaceBetween = currentGap;
        };
        this.#swiper = new window.Swiper(viewport, {
          a11y: { enabled: true, slideRole: 'listitem' },
          freeMode: reduced ? { enabled: false } : true,
          grabCursor: !reduced,
          loop: false,
          noSwipingSelector: '.swiper-no-swiping, button, input, select, textarea, label, [role="button"], [contenteditable="true"], [data-ac-quick-add-open], [data-ac-product-card-option]',
          preventClicks: true,
          preventClicksPropagation: true,
          slideToClickedSlide: false,
          slidesPerView: search ? 'auto' : compact ? 3.2 : 1.2,
          spaceBetween: gap(),
          speed: reduced ? 0 : 420,
          watchOverflow: true,
          breakpoints: {
            768: { slidesPerView: search ? 3 : compact ? 4.1 : 2.2 },
            1024: { slidesPerView: search ? 5 : compact ? 4.1 : 3.2 },
          },
          on: {
            init: syncSwiper,
            setTransition: (_instance, duration) => setProgressTransition(duration),
            setTranslate: syncSwiper,
            slideChange: syncSwiper,
            breakpoint: syncSwiper,
            touchStart: () => {
              setProgressTransition(0);
              this.#pointerDown = true;
              this.#momentumActive = false;
            },
            sliderFirstMove: () => this.#beginPointerMotion(),
            touchEnd: () => {
              this.#pointerDown = false;
              if (!this.#momentumActive) this.#queuePointerMotionEnd();
            },
            transitionStart: (instance) => {
              if (!this.hasAttribute('data-ac-product-rail-moving') || !(parseFloat(instance.wrapperEl.style.transitionDuration) > 0)) return;
              this.#momentumActive = true;
              this.#beginPointerMotion();
            },
            transitionEnd: () => {
              setProgressTransition(0);
              if (this.#swiper) syncSwiper(this.#swiper);
              this.#momentumActive = false;
              if (!this.#pointerDown) this.#queuePointerMotionEnd(0);
            },
            beforeResize: syncGap,
            resize: syncSwiper,
          },
        });

        const keydown = (event) => {
          if (event.target !== viewport || !this.#swiper) return;
          if (event.key === 'ArrowLeft') this.#swiper.slidePrev();
          else if (event.key === 'ArrowRight') this.#swiper.slideNext();
          else if (event.key === 'Home') this.#swiper.slideTo(0);
          else if (event.key === 'End') this.#swiper.slideTo(items.length - 1);
          else return;
          event.preventDefault();
        };
        const preventDrag = (event) => event.preventDefault();
        viewport.addEventListener('keydown', keydown);
        viewport.addEventListener('dragstart', preventDrag);
        this.#cleanup.push(() => viewport.removeEventListener('keydown', keydown));
        this.#cleanup.push(() => viewport.removeEventListener('dragstart', preventDrag));
      }

      if (!this.#swiper) {
        let maximum = 0;
        let step = 1;
        let rtl = false;
        const syncNative = () => {
          const ratio = maximum ? Math.abs(viewport.scrollLeft) / maximum : 0;
          const pages = Math.max(1, Math.ceil(maximum / step) + 1);
          sync(ratio, Math.round(ratio * (pages - 1)), pages);
          if (search) this.toggleAttribute('data-overflow', maximum > 1);
        };
        const measureNative = () => {
          maximum = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
          step = Math.max(1, items[0].getBoundingClientRect().width + (parseFloat(getComputedStyle(track).columnGap) || 0));
          rtl = getComputedStyle(viewport).direction === 'rtl';
          syncNative();
        };
        const keydown = (event) => {
          if (event.target !== viewport) return;
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          if (!maximum) return;
          let destination = viewport.scrollLeft;
          if (event.key === 'Home') destination = 0;
          else if (event.key === 'End') destination = rtl ? -maximum : maximum;
          else destination += event.key === 'ArrowRight' ? step : -step;
          event.preventDefault();
          viewport.scrollTo({ left: destination, behavior: reducedMotion() ? 'instant' : 'smooth' });
        };
        viewport.addEventListener('keydown', keydown);
        viewport.addEventListener('scroll', syncNative, { passive: true });
        const observer = new ResizeObserver(measureNative);
        observer.observe(viewport);
        this.#cleanup.push(() => viewport.removeEventListener('keydown', keydown));
        this.#cleanup.push(() => viewport.removeEventListener('scroll', syncNative));
        this.#cleanup.push(() => observer.disconnect());
        measureNative();
      }
      if (!compact && !search) {
        const timer = window.setTimeout(() => this.#initMotion(items), 0);
        this.#cleanup.push(() => window.clearTimeout(timer));
      }
    }

    #beginPointerMotion() {
      window.clearTimeout(this.#motionSettleTimer);
      this.#motionSettleTimer = 0;
      if (this.hasAttribute('data-ac-product-rail-moving')) return;
      this.setAttribute('data-ac-product-rail-moving', '');
      this.dispatchEvent(new CustomEvent('ac:product-rail-motion-start', { bubbles: true }));
    }

    #queuePointerMotionEnd(delay = 80) {
      window.clearTimeout(this.#motionSettleTimer);
      this.#motionSettleTimer = window.setTimeout(() => {
        this.#motionSettleTimer = 0;
        if (this.#pointerDown || this.#momentumActive) return;
        if (!this.hasAttribute('data-ac-product-rail-moving')) return;
        this.removeAttribute('data-ac-product-rail-moving');
        this.dispatchEvent(new CustomEvent('ac:product-rail-motion-end', { bubbles: true }));
      }, delay);
    }

    #initMotion(items) {
      if (this.#motionReady || reducedMotion() || !window.gsap || !window.ScrollTrigger) return;
      if (!this.isConnected || this.closest('[hidden]')) return;
      this.#motionReady = true;
      window.gsap.registerPlugin(window.ScrollTrigger);
      const title = this.querySelector('.ac-product-rail__header');
      const meta = [...this.querySelectorAll('[data-ac-product-rail-meta] > *')];
      const visibleMedia = items
        .slice(0, window.innerWidth < 768 ? 1 : window.innerWidth < 1024 ? 2 : 3)
        .map((item) => item.querySelector('.ac-product-card__image--primary img, .ac-product-card__placeholder-svg'))
        .filter(Boolean);
      this.#motion = window.gsap.context(() => {
        const timeline = window.gsap.timeline({ scrollTrigger: { trigger: this, start: 'top 75%', once: true } });
        if (visibleMedia.length) timeline.fromTo(visibleMedia, { scale: 1.08, transformOrigin: 'center' }, { scale: 1, duration: 1.1, ease: 'power2.out', stagger: 0.04 }, 0);
        if (title) timeline.fromTo(title, { scaleY: 0, transformOrigin: 'top' }, { scaleY: 1, duration: 0.7, ease: 'power1.out' }, 0);
        if (meta.length) timeline.fromTo(meta, { scaleY: 0, transformOrigin: 'top' }, { scaleY: 1, duration: 0.7, ease: 'power1.out', stagger: 0.04 }, 0.2);
      }, this);
    }

    #destroy() {
      window.clearTimeout(this.#motionSettleTimer);
      this.#motionSettleTimer = 0;
      this.#pointerDown = false;
      this.#momentumActive = false;
      this.removeAttribute('data-ac-product-rail-moving');
      const progress = this.querySelector('[data-ac-progress]');
      progress?.removeAttribute('data-ac-progress-animating');
      progress?.style.removeProperty('--ac-progress-motion-duration');
      for (const cleanup of this.#cleanup.splice(0)) cleanup();
      this.#motion?.revert?.();
      this.#motion = null;
      this.#motionReady = false;
      if (!this.#swiper) return;
      const list = this.querySelector('[data-ac-product-list]');
      const viewport = list?.querySelector('[data-ac-product-list-viewport]');
      const track = list?.querySelector('[data-ac-product-list-track]');
      const items = list ? [...list.querySelectorAll('.ac-product-list__item')] : [];
      this.#swiper.destroy(true, true);
      this.#swiper = null;
      list?.classList.remove('is-swiper');
      viewport?.classList.remove('swiper');
      viewport?.removeAttribute('aria-roledescription');
      track?.classList.remove('swiper-wrapper');
      for (const item of items) item.classList.remove('swiper-slide');
    }
  }

  if (!customElements.get('ac-product-rail')) customElements.define('ac-product-rail', AcProductRail);
})();
